import AppBar from '@material-ui/core/AppBar/AppBar';
import Button from '@material-ui/core/Button/Button';
import Dialog from '@material-ui/core/Dialog/Dialog';
import DialogContent from '@material-ui/core/DialogContent/DialogContent';
import FormControl from '@material-ui/core/FormControl/FormControl';
import InputLabel from '@material-ui/core/InputLabel/InputLabel';
import MenuItem from '@material-ui/core/MenuItem/MenuItem';
import Select from '@material-ui/core/Select/Select';
import Toolbar from '@material-ui/core/Toolbar/Toolbar';
import Typography from '@material-ui/core/Typography/Typography';
import { ArrowForwardIos } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { useStores } from 'app/hooks/useStores';
import { getFranchiseTagsApi } from 'app/main/apps/franchises/api';
import { selectUserFranchise } from 'app/main/apps/franchises/ducks/franchises.selector';
import DialogCloseButton from 'app/main/design-system/components/DialogCloseButton';
import { showMessage } from 'app/store/fuse/messageSlice';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { TagItemInDTO } from '../../type';

interface SelectScannerDialogProps {
  open: boolean;
  onClose: () => void;
}

const SelectScannerDialog: React.FC<SelectScannerDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const franchise = useSelector(selectUserFranchise);

  const [tags, setTags] = useState<TagItemInDTO[]>([]);
  const [selectedTagId, setSelectedTagId] = useState<string>(localStorage.getItem('selectedTagId') || '');

  const { currentStoreId } = useStores();

  const fetchTags = async () => {
    try {
      const response = await getFranchiseTagsApi(franchise?.id, currentStoreId);
      setTags(response.data);
      if (response.data.length === 1) {
        const { tagId } = response.data[0];
        setSelectedTagId(tagId);
        localStorage.setItem('selectedTagId', tagId);
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        dispatch(showMessage({ message: t('dialogs.selectScanner.fetchTagsError'), variant: 'error' }));
      } else if (open) {
        dispatch(showMessage({ message: t('dialogs.selectScanner.noTags'), variant: 'info' }));
      }
    }
  };

  const handleSelectTag = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    setSelectedTagId(value);
    localStorage.setItem('selectedTagId', value);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation_todo>
  useEffect(() => {
    if (open && currentStoreId && tags.length === 0) {
      fetchTags();
    }
  }, [open, currentStoreId]);

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="select-scanner-dialog" fullWidth maxWidth="xs">
      <AppBar position="static" elevation={0}>
        <Toolbar className="px-24 flex justify-between">
          <Typography variant="h6" color="inherit">
            {t('dialogs.selectScanner.title')}
          </Typography>
          <DialogCloseButton onClose={onClose} />
        </Toolbar>
      </AppBar>
      <DialogContent className="flex flex-col items-center space-y-24 p-24">
        {tags.length > 0 ? (
          <FormControl variant="outlined" className="w-full" disabled={!currentStoreId || !tags.length}>
            <InputLabel id="tag-selector-label" className="bg-white px-2">
              {t('basic.fields.tags')}
            </InputLabel>
            <Select
              id="tag-selector"
              label={t('basic.fields.tags')}
              labelWidth={100}
              labelId="tag-selector-label"
              onChange={handleSelectTag}
              value={selectedTagId && tags.length ? selectedTagId : ''}
            >
              {tags.map(tag => (
                <MenuItem key={tag.id} value={tag.tagId} selected={tag.tagId === selectedTagId}>
                  {tag.name || tag.tagId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Alert severity="warning" className="w-full">
            {t('dialogs.selectScanner.noTagsMessage')}
          </Alert>
        )}
        <Link
          to={`/stores/${currentStoreId}/tags/${selectedTagId}/scanner`}
          className="w-full"
          target="_blank"
          rel="noopener noreferrer"
        >
          <Button variant="contained" color="primary" disabled={!currentStoreId || !selectedTagId} className="w-full">
            {t('dialogs.selectScanner.accessToScanner')}
            <ArrowForwardIos fontSize="small" className="ml-4" />
          </Button>
        </Link>
      </DialogContent>
    </Dialog>
  );
};

export default SelectScannerDialog;
